import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  CircularProgress
} from '@mui/material';
import { useEffect, useState } from 'react';
import bg from './assets/Background.jpg';
import bgblurred from './assets/BlurredBackground.jpg';

const API_URL = 'https://script.google.com/macros/s/AKfycbz470YBjuxc9nILX2bvqSg8s2LZOdEGLtk6Uqa4RW7e2Q29h2ZVmi_JZnOh0b0PmPcJ/exec';

export default function AddressForm() {
  const params = new URLSearchParams(window.location.search);
  const guid = params.get('guid');

  const [addressedTo, setAddressedTo] = useState('');

  const [form, setForm] = useState({
    address: '',
    city: '',
    state: '',
    postcode: '',
    country: ''
  });

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    async function loadData() {
      try {

        if (!guid) {
          setError("missing-guid");
          setLoading(false);
          return;
        }


        const res = await fetch(`${API_URL}?guid=${guid}`);
        const data = await res.json();

        console.log("API response:", data);

        if (!data || data.error) {
          console.error("Invalid API response", data);

          setError("invalid-guid");
          setLoading(false);

          return;
        }

        setAddressedTo(data["Addressed To"] ?? "");

        setForm({
          address: data.Address ?? "",
          city: data.City ?? "",
          state: data.State ?? "",
          postcode: data.Postcode ?? "",
          country: data.Country ?? ""
        });

        setLoading(false);
      } catch (err) {

        console.error("Failed to load data", err);
        setError("load-failed");
        setLoading(false);

      }
    }

    loadData();
  }, [guid]);

  const handleChange = e =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitting(true);

    try {
      await fetch(API_URL, {
        method: 'POST',
        body: JSON.stringify({ guid, ...form }),
      });

      // ✅ Once saved, switch to thank-you state
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to submit address', err);
    } finally {
      setSubmitting(false);
    }
  };


  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          //backgroundColor: '#eeeee4',
          backgroundImage: `url(${bgblurred})`,
          transition: 'background-image 0.3s ease',
          backgroundSize: {
            xs: '375%',  // Mobile often needs more zoom
            md: '175%',
          },
          backgroundPosition: {
            xs: '65%',  // Mobile: top-right focus
            md: '80% 40%',  // Desktop: balanced right/top
          },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Optional overlay for contrast */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.25)'
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            textAlign: 'center',
            color: 'white'
          }}
        >
          <CircularProgress
            color="inherit"
            size={48}
            thickness={4}
          />

          <Typography sx={{ mt: 3 }} variant="body1">
            Loading your details…
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          //backgroundColor: '#eeeee4',
          backgroundImage: `url(${bgblurred})`,
          transition: 'background-image 0.3s ease',
          backgroundSize: {
            xs: '375%',  // Mobile often needs more zoom
            md: '175%',
          },
          backgroundPosition: {
            xs: '65%',  // Mobile: top-right focus
            md: '80% 40%',  // Desktop: balanced right/top
          },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2
          }}
        >
          <Card elevation={6} sx={{ maxWidth: 400 }}>
            <CardContent>
              <Typography variant="h6" align="center" gutterBottom>
                We couldn’t load this page
              </Typography>

              <Typography
                variant="body2"
                align="center"
                color="text.secondary"
              >
                {error === "missing-guid" &&
                  "This link appears to be incomplete. Please check your email and try again."}

                {error === "invalid-guid" &&
                  "This link is no longer valid or has already been used."}

                {error === "load-failed" &&
                  "Something went wrong while loading your details. Please try again later."}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#eeeee4',
        backgroundImage: `url(${bg})`,
        transition: 'background-image 0.3s ease',
        backgroundSize: {
          xs: '375%',  // Mobile often needs more zoom
          md: '175%',
        },
        backgroundPosition: {
          xs: '65%',  // Mobile: top-right focus
          md: '80% 40%',  // Desktop: balanced right/top
        },
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2
      }}
    >


      <Container maxWidth="xs">
        <Card
          elevation={8}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.75)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            borderRadius: 3
          }}
        >
          <CardContent>
            {submitted ? (

              <>
                <Typography variant="h5" align="center" gutterBottom>
                  Thank you{addressedTo ? `, ${addressedTo}` : ''}!
                </Typography>

                <Typography
                  variant="body1"
                  align="center"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  Your mailing address has been saved.
                  <br />
                  If your address changes, please come back and update your details.
                </Typography>

                <Typography
                  variant="body2"
                  align="center"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  We can’t wait to celebrate with you!
                </Typography>
              </>

            ) : (
              <>
                <Typography variant="h5" align="center" gutterBottom>
                  {addressedTo ? `Hi ${addressedTo},` : 'Hello,'}
                </Typography>

                <Typography
                  variant="body2"
                  align="center"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Please confirm your mailing address so your invitation arrives safely.
                </Typography>

                <Box component="form" onSubmit={handleSubmit}>
                  <TextField
                    label="Street Address"
                    name="address"
                    fullWidth
                    required
                    margin="normal"
                    value={form.address}
                    onChange={handleChange}
                    disabled={submitting}
                  />

                  <TextField
                    label="City"
                    name="city"
                    fullWidth
                    required
                    margin="normal"
                    value={form.city}
                    onChange={handleChange}
                    disabled={submitting}
                  />

                  <TextField
                    label="State / Province"
                    name="state"
                    fullWidth
                    margin="normal"
                    value={form.state}
                    onChange={handleChange}
                    disabled={submitting}
                  />

                  <TextField
                    label="Postcode"
                    name="postcode"
                    fullWidth
                    required
                    margin="normal"
                    value={form.postcode}
                    onChange={handleChange}
                    disabled={submitting}
                  />

                  <TextField
                    label="Country"
                    name="country"
                    fullWidth
                    required
                    margin="normal"
                    value={form.country}
                    onChange={handleChange}
                    disabled={submitting}
                  />


                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{ mt: 3 }}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Save Address'
                    )}
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}